import api from './api';
import { handleResponse, handleError } from './api';

/**
 * Portal v1 — applicant + member API surface.
 *
 * State machine values returned by /api/portal/state:
 *   "anonymous" | "no_profile" | "not_applied" |
 *   "pending" | "accepted" | "declined" | "not_yet"
 *
 * Routing logic on the frontend keys off `state` to render the right page.
 */
const portalService = {
  async getState() {
    try { return handleResponse(await api.get('/portal/state')); }
    catch (e) { return handleError(e); }
  },

  async listCities() {
    try { return handleResponse(await api.get('/portal/cities')); }
    catch (e) { return handleError(e); }
  },

  async getMyApplication() {
    try { return handleResponse(await api.get('/portal/applications/mine')); }
    catch (e) { return handleError(e); }
  },

  async submitApplication({ referrerText, pitchText, inviteCode }) {
    try {
      return handleResponse(await api.post('/portal/applications', { referrerText, pitchText, inviteCode: inviteCode || null }));
    } catch (e) { return handleError(e); }
  },

  // Portal admission invites — issued by members, claimed by would-be applicants.
  async createPortalInvite(label) {
    try { return handleResponse(await api.post('/portal/invites', { label: label || null })); }
    catch (e) { return handleError(e); }
  },

  async listMyPortalInvites() {
    try { return handleResponse(await api.get('/portal/invites/mine')); }
    catch (e) { return handleError(e); }
  },

  async revokePortalInvite(inviteId) {
    try { return handleResponse(await api.delete(`/portal/invites/${inviteId}`)); }
    catch (e) { return handleError(e); }
  },

  async previewPortalInvite(code) {
    try { return handleResponse(await api.get(`/portal/invites/preview/${encodeURIComponent(code)}`)); }
    catch (e) { return handleError(e); }
  },

  // Member-only
  async getMyCities() {
    try { return handleResponse(await api.get('/portal/me/cities')); }
    catch (e) { return handleError(e); }
  },

  async setMyCities(cities) {
    try { return handleResponse(await api.put('/portal/me/cities', { cities })); }
    catch (e) { return handleError(e); }
  },

  async getMyLocation() {
    try { return handleResponse(await api.get('/portal/me/location')); }
    catch (e) { return handleError(e); }
  },

  async setMyLocation({ latitude, longitude, label }) {
    try { return handleResponse(await api.put('/portal/me/location', { latitude, longitude, label })); }
    catch (e) { return handleError(e); }
  },

  // Signals
  async listSignals(cities = [], page = 1, pageSize = 50) {
    try {
      const cs = (cities || []).filter(Boolean).join(',');
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (cs) params.set('cities', cs);
      return handleResponse(await api.get(`/portal/signals?${params.toString()}`));
    } catch (e) { return handleError(e); }
  },

  // Signals the viewer is part of (hosting / opted-in / invited), any distance.
  async listMySignals() {
    try { return handleResponse(await api.get('/portal/signals/mine')); }
    catch (e) { return handleError(e); }
  },

  async getSignal(eventId) {
    try { return handleResponse(await api.get(`/portal/signals/${eventId}`)); }
    catch (e) { return handleError(e); }
  },

  async createSignal({ mode, cities, latitude, longitude, dateTimeUtc, signalText, capacity, costCents, isExternalTicketing = false, externalTicketUrl = null, externalTicketDisplayPrice = null, links = null, venueName = null, venueAddress = null }) {
    try {
      return handleResponse(await api.post('/portal/signals', {
        mode, cities, latitude, longitude, dateTimeUtc, signalText, capacity, costCents, isExternalTicketing, externalTicketUrl, externalTicketDisplayPrice, links, venueName, venueAddress,
      }));
    } catch (e) { return handleError(e); }
  },

  async updateSignal(eventId, { mode, cities, latitude, longitude, dateTimeUtc, signalText, capacity, costCents, isExternalTicketing = false, externalTicketUrl = null, externalTicketDisplayPrice = null, links = null, venueName = null, venueAddress = null }) {
    try {
      return handleResponse(await api.put(`/portal/signals/${eventId}`, {
        mode, cities, latitude, longitude, dateTimeUtc, signalText, capacity, costCents, isExternalTicketing, externalTicketUrl, externalTicketDisplayPrice, links, venueName, venueAddress,
      }));
    } catch (e) { return handleError(e); }
  },

  async deleteSignal(eventId) {
    try { return handleResponse(await api.delete(`/portal/signals/${eventId}`)); }
    catch (e) { return handleError(e); }
  },

  async respondImDown(eventId) {
    try { return handleResponse(await api.post(`/portal/signals/${eventId}/im-down`)); }
    catch (e) { return handleError(e); }
  },

  async withdrawResponse(eventId) {
    try { return handleResponse(await api.post(`/portal/signals/${eventId}/withdraw`)); }
    catch (e) { return handleError(e); }
  },

  async listSignalApplicants(eventId) {
    try { return handleResponse(await api.get(`/portal/signals/${eventId}/applicants`)); }
    catch (e) { return handleError(e); }
  },

  async reviewSignalApplicant(eventId, userId, action) {
    try { return handleResponse(await api.post(`/portal/signals/${eventId}/applicants/${userId}/review`, { action })); }
    catch (e) { return handleError(e); }
  },

  async inviteToSignal(eventId, userIds) {
    try { return handleResponse(await api.post(`/portal/signals/${eventId}/invites`, { userIds })); }
    catch (e) { return handleError(e); }
  },

  async listSignalInvites(eventId) {
    try { return handleResponse(await api.get(`/portal/signals/${eventId}/invites`)); }
    catch (e) { return handleError(e); }
  },

  async respondToSignalInvite(eventId, accept) {
    try { return handleResponse(await api.post(`/portal/signals/${eventId}/invites/respond`, { accept })); }
    catch (e) { return handleError(e); }
  },

  async searchPortalMembers(q, limit = 20) {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (q) params.set('q', q);
      return handleResponse(await api.get(`/portal/members/search?${params.toString()}`));
    } catch (e) { return handleError(e); }
  },
};

export default portalService;
